import {Injectable} from '@angular/core';
import {ActivatedRoute, ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot} from '@angular/router';
import {Observable} from "rxjs";
import {UserState} from "./user-state";
import {User} from "./user";
import * as fromStore from "../store";
import {Store} from '@ngrx/store';
import {filter, first, switchMap} from 'rxjs/operators';
import {combineLatest} from 'rxjs';
import {UserSubscriptionCategory, UserSubscriptionIds} from "./user-subscription";
import {RouteData} from "../shared/services/route-data/route-data";
import {clientRoles} from "./user-roles-actions";
import {RouteDataService} from "../shared/services/route-data/route-data.service";
import {GoogleAnalyticsService} from "../shared/services/google-analytics/google-analytics.service";


@Injectable()
export class UserStateGuard implements CanActivate {
  public userSubscriptionIds: any = UserSubscriptionIds;
  private currentWorkspace: any;
  private user: User;

  constructor(
    private _router: Router,
    private _store: Store<fromStore.DbStoreState>,
    private _route: ActivatedRoute,
    private _routeDataService: RouteDataService,
    private _googleAnalyticsService: GoogleAnalyticsService
  ) {

    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getCurrentWorkspace),
          this._store.select(fromStore.getUserSubscriptionState),
          this._store.select(fromStore.getUser)
        )),
        first()
      )
      .subscribe(([currentWorkspace, userSubscription, user]: any) => {
        this.currentWorkspace = currentWorkspace;
        this.user = user;
        const userSubscriptionState = userSubscription;
        if (userSubscriptionState && this.currentWorkspace && (userSubscriptionState.noActiveSubscription || userSubscriptionState.subscriptionExpired) && this.currentWorkspace && !['invited'].includes(this.currentWorkspace.status)) {
          this._router.navigate(['/workspace/subscription-plan']);
          return;
        } else if (this.currentWorkspace && ['invited'].includes(this.currentWorkspace.status)) {
          this._router.navigate(['/upgrade-plan']);
          return;
        }
      });
  }

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
    return new Promise<boolean>((resolve, reject) => {
      const routeData: RouteData = new RouteData(next.data);

      this._store.select(fromStore.getUserLoaded)
        .pipe(
          filter((loaded: boolean) => loaded),
          switchMap(() => combineLatest(
            this._store.select(fromStore.getUser),
            this._store.select(fromStore.getUserSubscriptionState),
            this._store.select(fromStore.getCurrentWorkspace),
          )),
          first()
        )
          .subscribe(([currentUser, userSubscription, currentWorkspace]: any) => {
          const queryParams: any = next.queryParams;
          const user: User = currentUser;
          const userSubscriptionState: UserState = userSubscription;
          this.currentWorkspace = currentWorkspace;

          if (!user) {
            this._router.navigate(['/websites']);
            resolve(false);
            return;
          }
          else if (userSubscriptionState?.subscriptionBillingType === 'not-paid' && userSubscriptionState?.stripeSbcrstatus === 'suspended') {
            // Prevent loop: only redirect if not already on /websites
            if (state.url !== '/websites') {
              this._router.navigate(['/websites']);
              resolve(false);
            } else {
              resolve(true);
            }
            return;
          }
          else if (user?.apiResellerFree && user?.hostingTrialExpired) {
            this._router.navigate(['/upgrade-plan']);
            resolve(false);
            return;
          }
          else if (user && user.planType === UserSubscriptionCategory.STARTER  && user.hostingTrialExpired && routeData.data.pageId !== "paypal-waiting") {
            if (queryParams.dbDemoCampaign || new URL(window.location.href).searchParams.get("dbDemoCampaign") || queryParams.emailCampaign || new URL(window.location.href).searchParams.get("emailCampaign")) {
              resolve(true);
              return;
            }
            this._router.navigate(['/upgrade-plan']);
            resolve(false);
          }
          else if (user && this.currentWorkspace && user.workspaces.find(el => el.workspaceId === this.currentWorkspace.workspaceId && ['invited'].includes(el.status)) && routeData.data.pageId !== "paypal-waiting") {
            this._router.navigate(['/upgrade-plan']);
            resolve(false);
          }
          else if ( user && user.planType === UserSubscriptionCategory.STARTER && window.innerWidth > 1024) {
            resolve(true);
            return;
          }
          else if (userSubscriptionState && (userSubscriptionState.noActiveSubscription || userSubscriptionState.subscriptionExpired)
            && routeData.data.pageId !== "workspace" && routeData.data.pageId !== "paypal-waiting" && this.currentWorkspace && !['invited'].includes(this.currentWorkspace.status)) {
            if (userSubscriptionState?.hasActiveDomainAgreement) {
              this._router.navigate(['/domains-handler/portfolio']);
              resolve(false);
              return;
            }
            this._router.navigate(['/workspace/subscription-plan']);
            resolve(false);
            return;
          } else if (this.currentWorkspace && ['invited'].includes(this.currentWorkspace.status)) {
            this._router.navigate(['/upgrade-plan']);
            return;
          }

          resolve(true);
        });
    });
  }

}
